// src/components/TrainingLibrary/CycleTemplatesPanel.js
import React, { useEffect, useMemo, useState } from "react";
import BackToDashboardBtn from '../TrainerDashboard/BackToDashboardBtn';
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import * as XLSX from "xlsx";

import iconRun from "../../assets/icons/running.png";
import iconBike from "../../assets/icons/cycling.png";
import iconSwim from "../../assets/icons/swim.png";
import iconStrength from "../../assets/icons/strength.png";
import iconWalk from "../../assets/icons/walk.png";
import iconRest from "../../assets/icons/dayoff.png";
import iconOther from "../../assets/icons/other.png";
import { useTranslation } from "../../i18n";

/* ===== URL plantilla / guía ===== */
const TEMPLATE_GUIDE_PATH = "/templates/tzone_plan_import_template_with_guide.xlsx";
const TEMPLATE_GUIDE_URL = `${window.location.origin}${TEMPLATE_GUIDE_PATH}`;

/* ===== Auth helpers (NO ROMPE NADA) ===== */
function readTokenBestEffort() {
  const candidates = [
    localStorage.getItem("token"),
    localStorage.getItem("authToken"),
    localStorage.getItem("accessToken"),
    localStorage.getItem("jwt"),
    sessionStorage.getItem("token"),
    sessionStorage.getItem("authToken"),
    sessionStorage.getItem("accessToken"),
    sessionStorage.getItem("jwt"),
  ];

  for (const raw of candidates) {
    const tokenCandidate = normalizeToken(raw);
    if (isJwtLike(tokenCandidate)) return tokenCandidate;
  }

  const storages = [localStorage, sessionStorage];
  for (const st of storages) {
    try {
      for (let i = 0; i < st.length; i++) {
        const k = st.key(i);
        const raw = st.getItem(k);
        if (!raw) continue;

        if (String(raw).trim().startsWith("{")) {
          const parsed = safeJson(raw);
          const inner =
            normalizeToken(parsed?.token) ||
            normalizeToken(parsed?.accessToken) ||
            normalizeToken(parsed?.authToken) ||
            normalizeToken(parsed?.jwt) ||
            "";
          if (isJwtLike(inner)) return inner;
        }

        const tokenCandidate = normalizeToken(raw);
        if (isJwtLike(tokenCandidate)) return tokenCandidate;
      }
    } catch {}
  }

  return "";
}

function safeJson(s) {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function normalizeToken(raw) {
  if (!raw) return "";
  let tokenValue = String(raw).trim();

  if (
    (tokenValue.startsWith('"') && tokenValue.endsWith('"')) ||
    (tokenValue.startsWith("'") && tokenValue.endsWith("'"))
  ) {
    tokenValue = tokenValue.slice(1, -1).trim();
  }

  tokenValue = tokenValue.replace(/^bearer\s+/i, "").trim();
  tokenValue = tokenValue.replace(/^bearer\s+/i, "").trim();

  if (tokenValue === "null" || tokenValue === "undefined") return "";
  return tokenValue;
}

function isJwtLike(s) {
  if (!s) return false;
  return /^[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+$/.test(String(s).trim());
}

function authConfig() {
  const token = readTokenBestEffort();
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

/* ===== Sombras locales ===== */
const keyById = (id) => `tpl-shadow:id:${id}`;
const keyByName = (name, level, sport, distanceCategory) =>
  `tpl-shadow:name:${(name || "").trim()}|${level || ""}|${sport || ""}|${(distanceCategory || "").trim()}`;

function saveShadowById(id, payload) {
  try {
    localStorage.setItem(
      keyById(id),
      JSON.stringify({
        defaultDays: Number(payload?.defaultDays || 0),
        days: Array.isArray(payload?.days) ? payload.days : [],
        savedAt: Date.now(),
      })
    );
  } catch {}
}

function saveShadowByName(meta, payload) {
  try {
    localStorage.setItem(
      keyByName(meta.name, meta.level, meta.sport, meta.distanceCategory),
      JSON.stringify({
        defaultDays: Number(payload?.defaultDays || 0),
        days: Array.isArray(payload?.days) ? payload.days : [],
        savedAt: Date.now(),
      })
    );
  } catch {}
}

function loadShadowForTemplate(tpl) {
  try {
    const byId = localStorage.getItem(keyById(tpl?._id || tpl?.id || ""));
    if (byId) return JSON.parse(byId);
  } catch {}
  try {
    const byName = localStorage.getItem(
      keyByName(tpl?.name, tpl?.level, tpl?.sport, tpl?.distanceCategory)
    );
    if (byName) return JSON.parse(byName);
  } catch {}
  return null;
}

function removeShadowForId(id) {
  try {
    localStorage.removeItem(keyById(id));
  } catch {}
}

/* ===== Modal deporte ===== */
function SportPickerModal({ open, value, onPick, onClose, sports, title }) {
  if (!open) return null;
  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100"
      style={{ background: "rgba(0,0,0,0.35)", zIndex: 2000 }}
      onClick={onClose}
    >
      <div
        className="position-absolute start-50 top-50 translate-middle"
        style={{
          width: 380,
          maxWidth: "95vw",
          background: "#fff",
          borderRadius: 8,
          boxShadow: "0 10px 30px rgba(0,0,0,.2)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
          <h5 className="m-0">{title}</h5>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="p-3 d-grid" style={{ gap: 10 }}>
          {sports.map((s) => (
            <button
              key={s.value}
              type="button"
              className="w-100 d-flex align-items-center justify-content-between text-white"
              style={{ background: s.bg, border: "none", borderRadius: 8, padding: "10px 14px" }}
              onClick={() => {
                onPick(s.value);
                onClose();
              }}
            >
              <span className="d-flex align-items-center gap-3">
                <img
                  src={s.icon}
                  alt={s.label}
                  width={28}
                  height={28}
                  style={{ objectFit: "contain", filter: "brightness(0) invert(1)" }}
                />
                <strong>{s.label}</strong>
              </span>
              {value === s.value && <span>✓</span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ===== Panel principal ===== */
export default function CycleTemplatesPanel() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const SPORTS = [
    { value: "running", label: t("activityLabels.running"), icon: iconRun, bg: "#7b2cbf" },
    { value: "cycling", label: t("activityLabels.cycling"), icon: iconBike, bg: "#2ea44f" },
    { value: "swimming", label: t("activityLabels.swimming"), icon: iconSwim, bg: "#00a8e8" },
    { value: "rest", label: t("activityLabels.rest"), icon: iconRest, bg: "#00bcd4" },
    { value: "strength", label: t("activityLabels.strength"), icon: iconStrength, bg: "#6c2bd9" },
    { value: "walking", label: t("activityLabels.walking"), icon: iconWalk, bg: "#ffa000" },
    { value: "other", label: t("activityLabels.other"), icon: iconOther, bg: "#374151" },
  ];

  const sportByValue = (v) => SPORTS.find((s) => s.value === v) || SPORTS[0];

  const METRICS = [
    { value: "duration", label: t("trainerTemplates.metricDuration") },
    { value: "distance", label: t("trainerTemplates.metricDistance") },
    { value: "elevation", label: t("trainerTemplates.metricElevation") },
    { value: "tss", label: t("trainerTemplates.metricTss") },
  ];

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [newTemplate, setNewTemplate] = useState({
    name: "",
    sport: "running",
    distanceCategory: "",
    level: "intermediate",
    defaultDays: 14,
    notes: "",
  });

  const [editTemplateId, setEditTemplateId] = useState(null);
  const isEditing = !!editTemplateId;
  const [loadingEditId, setLoadingEditId] = useState(null);

  const [activities, setActivities] = useState([]);
  const emptyAct = {
    id: null,
    day: 1,
    sport: "running",
    title: "",
    metric: "duration",
    value: "",
    description: "",
  };
  const [formAct, setFormAct] = useState(emptyAct);
  const [editingId, setEditingId] = useState(null);

  const [sportModalOpen, setSportModalOpen] = useState(false);
  const [actSportModalOpen, setActSportModalOpen] = useState(false);
  const [infoMsg, setInfoMsg] = useState("");

  const sportIcon = (v) => sportByValue(v).icon;
  const sportLabel = (v) => sportByValue(v).label;

  const levelLabel = (level) => {
    if (level === "novice") return t("trainingPlans.levelNovice");
    if (level === "advanced") return t("trainingPlans.levelAdvanced");
    return t("trainingPlans.levelIntermediate");
  };

  /* ===== Listado ===== */
  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await api.get(`/cycles/templates`, authConfig());
      const list = Array.isArray(res.data) ? res.data : [];
      setTemplates(list);

      return list;
    } catch (e) {
      console.error(e);

      const status = e?.response?.status;
      if (status === 401) {
        setError(t("cycleTemplatesPanel.errors.unauthenticatedReload"));
      } else {
        setError(t("cycleTemplatesPanel.errors.loadingTemplates"));
      }

      setTemplates([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  /* ===== Helpers actividades ===== */
  const resetActForm = () => {
    setFormAct({ ...emptyAct, day: 1, sport: newTemplate.sport });
    setEditingId(null);
  };

  const addActivity = () => {
    if (!formAct.title.trim()) {
      return alert(t("cycleTemplatesPanel.alerts.activityTitleRequired"));
    }
    if (!formAct.description.trim()) {
      return alert(t("cycleTemplatesPanel.alerts.activityDescriptionRequired"));
    }
    if (formAct.value === "" || formAct.value === null) {
      return alert(t("cycleTemplatesPanel.alerts.activityValueRequired"));
    }

    const id = Date.now().toString(36);
    setActivities((prev) => [...prev, { ...formAct, id }]);

    const maxDays = Number(newTemplate.defaultDays) || 1;
    const nextDay = Math.min((Number(formAct.day) || 1) + 1, maxDays);
    setFormAct((prev) => ({ ...emptyAct, day: nextDay, sport: prev.sport }));
    setEditingId(null);
  };

  const startEditActivity = (id) => {
    const a = activities.find((x) => x.id === id);
    if (!a) return;
    setEditingId(id);
    setFormAct({ ...a });
  };

  const saveEditActivity = () => {
    if (!formAct.title.trim() || !formAct.description.trim() || formAct.value === "") {
      return alert(t("cycleTemplatesPanel.alerts.completeTitleDescriptionValue"));
    }
    setActivities((prev) => prev.map((x) => (x.id === editingId ? { ...formAct } : x)));
    resetActForm();
  };

  const removeActivity = (id) => setActivities((prev) => prev.filter((x) => x.id !== id));

  const groupedByDay = useMemo(() => {
    const map = new Map();
    for (let d = 1; d <= Number(newTemplate.defaultDays || 0); d++) map.set(d, []);
    for (const a of activities) {
      const d = Math.max(1, Number(a.day) || 1);
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(a);
    }
    return map;
  }, [activities, newTemplate.defaultDays]);

  /* ===== Inferencias ===== */
  function inferMetricBySportWhenNumericOnly(sport) {
    switch (sport) {
      case "running":
      case "cycling":
      case "walking":
        return "distance";
      case "swimming":
      case "strength":
      case "rest":
      default:
        return "duration";
    }
  }

  /* ===== Importar Excel ===== */
  const handleExcelImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
      if (!rows.length) return alert(t("cycleTemplatesPanel.alerts.excelNoRows"));

      const normalize = (s) => String(s || "").trim().toLowerCase();
      const keys = Object.keys(rows[0] || {});
      const guessCol = (names) => keys.find((k) => names.some((n) => normalize(k).includes(n))) || "";

      const colSport = guessCol(["deporte", "sport"]);
      const colTitle = guessCol(["título", "titulo", "tarea", "sesión", "sesion", "title", "task"]);
      const colDesc = guessCol(["descripción", "descripcion", "detalle", "description", "detail"]);
      const colNumeric =
        keys.find((k) => /^unnamed:\s*6$/i.test(k)) ||
        guessCol(["valor", "distancia", "duración", "duracion", "value", "distance", "duration"]);

      let sequentialDay = 1;
      const parsed = [];
      for (const r of rows) {
        const day = sequentialDay++;
        const rawTitle = String(r[colTitle] || "").trim();
        const rawDesc = String(r[colDesc] || "").trim();

        let sport = normalize(r[colSport]);
        if (!sport) {
          const blob = `${rawTitle} ${rawDesc}`.toLowerCase();
          if (/\bnad(a|ar|o)\b|swim/.test(blob)) sport = "swimming";
          else if (/\bfuerza|gym|strength\b/.test(blob)) sport = "strength";
          else if (/\b(bike|cicl|rodillo|cycling)\b/.test(blob)) sport = "cycling";
          else if (/\bdescanso|rest\b/.test(blob)) sport = "rest";
          else if (/\bcaminar|caminata|walk\b/.test(blob)) sport = "walking";
          else sport = "running";
        } else {
          if (/corre|run/.test(sport)) sport = "running";
          else if (/cicl|bike|cycl/.test(sport)) sport = "cycling";
          else if (/nad|swim/.test(sport)) sport = "swimming";
          else if (/fuerza|strength/.test(sport)) sport = "strength";
          else if (/descanso|rest/.test(sport)) sport = "rest";
          else if (/camin|walk/.test(sport)) sport = "walking";
          else sport = "other";
        }

        let rawVal = r[colNumeric];
        let valueText = rawVal != null ? String(rawVal).trim() : "";
        if (!valueText) {
          const anyNumber = Object.values(r)
            .map((v) => String(v || ""))
            .join(" ")
            .match(/(\d+(?:[.,]\d+)?)/);
          if (anyNumber) valueText = anyNumber[1].replace(",", ".");
        }

        let metric = inferMetricBySportWhenNumericOnly(sport);
        const low = valueText.toLowerCase();
        if (/tss/.test(low)) metric = "tss";
        else if (/min|\bh:|^\d+:\d+/.test(low)) metric = "duration";
        else if (/\b\d+\s*m\b/.test(low) && !/km/.test(low)) metric = "elevation";
        else if (/km\b/.test(low)) metric = "distance";
        else if (!isNaN(Number(valueText))) metric = inferMetricBySportWhenNumericOnly(sport);

        parsed.push({
          id: randomId(),
          day,
          sport,
          title: rawTitle || t("trainerTemplates.untitled"),
          metric,
          value: valueText || "",
          description: rawDesc,
        });
      }

      setActivities(parsed);
      setNewTemplate((tpl) => ({ ...tpl, defaultDays: parsed.length }));
      alert(t("cycleTemplatesPanel.alerts.importedActivities", { count: parsed.length, days: parsed.length }));
    } catch (err) {
      console.error(err);
      alert(t("cycleTemplatesPanel.alerts.excelReadError"));
    } finally {
      e.target.value = "";
    }
  };

  /* ===== Payload ===== */
  function buildBodyFromState(resolvedDays) {
    const map = new Map();
    for (let d = 1; d <= resolvedDays; d++) map.set(d, []);
    for (const a of activities) {
      const d = Math.max(1, Number(a.day) || 1);
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(a);
    }
    const days = [];
    map.forEach((list, dayNumber) => {
      if (!list.length) return;
      days.push({
        day: Number(dayNumber),
        activities: list.map((a) => ({
          title: a.title.trim(),
          sport: a.sport,
          plannedType: a.metric,
          plannedValue: String(a.value),
          description: a.description.trim(),
        })),
      });
    });
    return {
      name: newTemplate.name,
      sport: newTemplate.sport,
      distanceCategory: newTemplate.distanceCategory,
      level: newTemplate.level,
      defaultDays: resolvedDays,
      notes: newTemplate.notes,
      days,
    };
  }

  /* ===== Crear ===== */
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTemplate.name.trim()) {
      return alert(t("cycleTemplatesPanel.alerts.planNameRequired"));
    }
    if (activities.length === 0) {
      return alert(t("cycleTemplatesPanel.alerts.addAtLeastOneActivityToTemplate"));
    }

    let resolvedDays = Number(newTemplate.defaultDays) || 0;
    let maxDay = 0;
    activities.forEach((a) => {
      maxDay = Math.max(maxDay, Math.max(1, Number(a.day) || 0));
    });
    if (resolvedDays <= 0) resolvedDays = maxDay || 1;
    if (resolvedDays < maxDay) resolvedDays = maxDay;

    const body = buildBodyFromState(resolvedDays);

    try {
      setError("");
      const res = await api.post(`/cycles/templates`, body, authConfig());

      const createdId = res?.data?._id || res?.data?.id;
      if (createdId) saveShadowById(createdId, body);
      saveShadowByName(body, body);

      const list = await fetchTemplates();
      if (!createdId) {
        const match = list
          .filter((tpl) => (tpl.name || "").trim() === body.name.trim())
          .sort(
            (a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0)
          )[0];
        if (match?._id) saveShadowById(match._id, body);
      }

      setEditTemplateId(null);
      setNewTemplate({
        name: "",
        sport: "running",
        distanceCategory: "",
        level: "intermediate",
        defaultDays: 14,
        notes: "",
      });
      setActivities([]);
      resetActForm();
      setInfoMsg("");
      alert(t("cycleTemplatesPanel.alerts.templateCreated"));
    } catch (e2) {
      console.error(e2);
      const status = e2?.response?.status;
      if (status === 401) {
        setError(t("cycleTemplatesPanel.errors.unauthenticatedCreate"));
      } else {
        setError(t("cycleTemplatesPanel.errors.createTemplate"));
      }
    }
  };

  /* ===== Editar (doc + sombra) ===== */
  async function handleEdit(templateId) {
    try {
      setLoadingEditId(templateId);
      setInfoMsg("");
      setError("");

      const { data } = await api.get(`/cycles/templates/${templateId}`, authConfig());

      const totalDaysFromPlan =
        Array.isArray(data?.days) && data.days.length
          ? data.days.reduce((m, d) => Math.max(m, Math.max(1, Number(d?.day ?? 0))), 0)
          : 0;

      setNewTemplate({
        name: data?.name || "",
        sport: data?.sport || "running",
        distanceCategory: data?.distanceCategory || "",
        level: data?.level || "intermediate",
        defaultDays: Number(data?.defaultDays || totalDaysFromPlan || 14),
        notes: data?.notes || "",
      });

      let acts = [];
      (Array.isArray(data?.days) ? data.days : []).forEach((d) => {
        const dayNum = Math.max(1, Number(d?.day ?? 1));
        (Array.isArray(d?.activities) ? d.activities : []).forEach((a) =>
          acts.push({
            id: randomId(),
            day: dayNum,
            sport: a?.sport || data?.sport || "running",
            title: a?.title || "",
            metric: a?.plannedType || a?.metric || "duration",
            value: a?.plannedValue ?? a?.value ?? "",
            description: a?.description || "",
          })
        );
      });

      if (!acts.length) {
        const shadow = loadShadowForTemplate({ _id: templateId, ...data });
        if (shadow && Array.isArray(shadow.days) && shadow.days.length) {
          shadow.days.forEach((d) => {
            const dayNum = Math.max(1, Number(d?.day ?? 1));
            (Array.isArray(d?.activities) ? d.activities : []).forEach((a) =>
              acts.push({
                id: randomId(),
                day: dayNum,
                sport: a?.sport || data?.sport || "running",
                title: a?.title || "",
                metric: a?.plannedType || a?.metric || "duration",
                value: a?.plannedValue ?? a?.value ?? "",
                description: a?.description || "",
              })
            );
          });
          const maxDay = acts.reduce((m, a) => Math.max(m, Math.max(1, Number(a.day) || 1)), 1);
          setNewTemplate((tpl) => ({
            ...tpl,
            defaultDays: Math.max(Number(tpl.defaultDays || 1), maxDay),
          }));
        }
      }

      setActivities(acts);
      if (!acts.length) {
        setInfoMsg(t("cycleTemplatesPanel.serverPlanMissing"));
      }

      setEditTemplateId(templateId);
      resetActForm();
      requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
    } catch (err) {
      console.error("[EDIT] Error cargando plantilla:", err);
      const status = err?.response?.status;
      if (status === 401) {
        setError(t("cycleTemplatesPanel.errors.unauthenticatedEdit"));
      } else {
        alert(t("cycleTemplatesPanel.alerts.editLoadError"));
      }
    } finally {
      setLoadingEditId(null);
    }
  }

  /* ===== Guardar cambios ===== */
  async function handleUpdate(e) {
    e.preventDefault();
    if (!newTemplate.name.trim()) {
      return alert(t("cycleTemplatesPanel.alerts.planNameRequired"));
    }
    if (activities.length === 0) {
      return alert(t("cycleTemplatesPanel.alerts.addAtLeastOneActivity"));
    }

    let resolvedDays = Number(newTemplate.defaultDays) || 0;
    let maxDay = 0;
    activities.forEach((a) => {
      maxDay = Math.max(maxDay, Math.max(1, Number(a.day) || 0));
    });
    if (resolvedDays <= 0) resolvedDays = maxDay || 1;
    if (resolvedDays < maxDay) resolvedDays = maxDay;

    const body = buildBodyFromState(resolvedDays);

    try {
      setError("");
      await api.put(`/cycles/templates/${editTemplateId}`, body, authConfig());
      saveShadowById(editTemplateId, body);
      saveShadowByName({ ...newTemplate }, body);

      setEditTemplateId(null);
      setNewTemplate({
        name: "",
        sport: "running",
        distanceCategory: "",
        level: "intermediate",
        defaultDays: 14,
        notes: "",
      });
      setActivities([]);
      resetActForm();
      setInfoMsg("");
      fetchTemplates();
      alert(t("cycleTemplatesPanel.alerts.templateUpdated"));
    } catch (err) {
      console.error(err);
      const status = err?.response?.status;
      if (status === 401) {
        setError(t("cycleTemplatesPanel.errors.unauthenticatedSave"));
      } else {
        alert(t("cycleTemplatesPanel.alerts.updateError"));
      }
    }
  }

  function handleCancelEdit() {
    setEditTemplateId(null);
    setNewTemplate({
      name: "",
      sport: "running",
      distanceCategory: "",
      level: "intermediate",
      defaultDays: 14,
      notes: "",
    });
    setActivities([]);
    setInfoMsg("");
    resetActForm();
  }

  return (
    <div className="container py-4">
      <div style={{ marginBottom:12 }}><BackToDashboardBtn /></div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="m-0">{t("cycleTemplatesPanel.title")}</h2>

        <div className="d-flex gap-2">
          <a
            className="btn btn-outline-secondary"
            href={TEMPLATE_GUIDE_URL}
            download
            title={t("cycleTemplatesPanel.downloadTemplateTitle")}
          >
            {t("cycleTemplatesPanel.downloadTemplate")}
          </a>

          <label className="btn btn-outline-primary mb-0">
            {t("cycleTemplatesPanel.importExcel")}
            <input type="file" accept=".xlsx,.xls" hidden onChange={handleExcelImport} />
          </label>


        </div>
      </div>

      <form className="mb-4 p-3 border rounded bg-light" onSubmit={(e) => e.preventDefault()}>
        <h5 className="mb-3">
          {isEditing ? t("cycleTemplatesPanel.editTemplate") : t("cycleTemplatesPanel.createTemplate")}
        </h5>

        <div className="row g-2">
          <div className="col-md-4">
            <input
              type="text"
              className="form-control"
              placeholder={t("cycleTemplatesPanel.planName")}
              value={newTemplate.name}
              onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
              required
            />
          </div>
          <div className="col-md-3">
            <button
              type="button"
              className="w-100 btn btn-outline-secondary d-flex align-items-center justify-content-between"
              onClick={() => setSportModalOpen(true)}
            >
              <span className="d-flex align-items-center gap-2">
                <img src={sportIcon(newTemplate.sport)} alt={sportLabel(newTemplate.sport)} width={18} height={18} />
                {sportLabel(newTemplate.sport)}
              </span>
              <span>▾</span>
            </button>
          </div>
          <div className="col-md-3">
            <input
              type="text"
              className="form-control"
              placeholder={t("cycleTemplatesPanel.distanceCategory")}
              value={newTemplate.distanceCategory}
              onChange={(e) => setNewTemplate({ ...newTemplate, distanceCategory: e.target.value })}
            />
          </div>
          <div className="col-md-2">
            <select
              className="form-select"
              value={newTemplate.level}
              onChange={(e) => setNewTemplate({ ...newTemplate, level: e.target.value })}
            >
              <option value="novice">{t("trainingPlans.levelNovice")}</option>
              <option value="intermediate">{t("trainingPlans.levelIntermediate")}</option>
              <option value="advanced">{t("trainingPlans.levelAdvanced")}</option>
            </select>
          </div>
        </div>

        <div className="row g-2 mt-2">
          <div className="col-md-3">
            <input
              type="number"
              min={1}
              className="form-control"
              placeholder={t("cycleTemplatesPanel.totalDays")}
              value={newTemplate.defaultDays}
              onChange={(e) => setNewTemplate({ ...newTemplate, defaultDays: Number(e.target.value) })}
            />
          </div>
          <div className="col-md-9">
            <input
              type="text"
              className="form-control"
              placeholder={t("cycleTemplatesPanel.notes")}
              value={newTemplate.notes}
              onChange={(e) => setNewTemplate({ ...newTemplate, notes: e.target.value })}
            />
          </div>
        </div>

        <div className="mt-4">
          <div className="d-flex align-items-center justify-content-between">
            <h6 className="m-0">{t("cycleTemplatesPanel.activities")}</h6>
            <small className="text-muted">
              {t("cycleTemplatesPanel.totalActivities")}: {activities.length}
            </small>
          </div>

          {infoMsg && (
            <div className="alert alert-info mt-2" role="alert">
              {infoMsg}
            </div>
          )}

          {isEditing && activities.length === 0 && !infoMsg && (
            <div className="alert alert-info mt-2" role="alert">
              {t("cycleTemplatesPanel.noPlanningYet")}
            </div>
          )}

          <div className="mt-2 p-2 border rounded">
            <div className="row g-2">
              <div className="col-md-2">
                <label className="form-label mb-1">{t("cycleTemplatesPanel.day")}</label>
                <input
                  type="number"
                  min={1}
                  max={Number(newTemplate.defaultDays || 1)}
                  className="form-control"
                  value={formAct.day}
                  onChange={(e) => setFormAct({ ...formAct, day: Number(e.target.value) })}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label mb-1">{t("cycleTemplatesPanel.sport")}</label>
                <button
                  type="button"
                  className="w-100 btn btn-outline-secondary d-flex align-items-center justify-content-between"
                  onClick={() => setActSportModalOpen(true)}
                >
                  <span className="d-flex align-items-center gap-2">
                    <img src={sportIcon(formAct.sport)} alt={sportLabel(formAct.sport)} width={18} height={18} />
                    {sportLabel(formAct.sport)}
                  </span>
                  <span>▾</span>
                </button>
              </div>
              <div className="col-md-7">
                <label className="form-label mb-1">{t("cycleTemplatesPanel.activityTitle")}</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("cycleTemplatesPanel.activityTitlePlaceholder")}
                  value={formAct.title}
                  onChange={(e) => setFormAct({ ...formAct, title: e.target.value })}
                />
              </div>
            </div>

            <div className="row g-2 mt-1">
              <div className="col-md-4">
                <label className="form-label mb-1">{t("cycleTemplatesPanel.metric")}</label>
                <select
                  className="form-select"
                  value={formAct.metric}
                  onChange={(e) => setFormAct({ ...formAct, metric: e.target.value })}
                >
                  {METRICS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label mb-1">{t("cycleTemplatesPanel.value")}</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("cycleTemplatesPanel.valuePlaceholder")}
                  value={formAct.value}
                  onChange={(e) => setFormAct({ ...formAct, value: e.target.value })}
                />
              </div>
              <div className="col-md-5">
                <label className="form-label mb-1">{t("cycleTemplatesPanel.description")}</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("cycleTemplatesPanel.activityDescriptionPlaceholder")}
                  value={formAct.description}
                  onChange={(e) => setFormAct({ ...formAct, description: e.target.value })}
                />
              </div>
            </div>

            <div className="mt-2 d-flex gap-2">
              {editingId ? (
                <>
                  <button type="button" className="btn btn-success" onClick={saveEditActivity}>
                    {t("common.save")}
                  </button>
                  <button type="button" className="btn btn-outline-secondary" onClick={resetActForm}>
                    {t("common.cancel")}
                  </button>
                </>
              ) : (
                <button type="button" className="btn btn-primary" onClick={addActivity}>
                  {t("cycleTemplatesPanel.addActivity")}
                </button>
              )}
            </div>
          </div>

          <div className="mt-3">
            {[...groupedByDay.keys()]
              .sort((a, b) => Number(a) - Number(b))
              .map((day) => {
                const list = groupedByDay.get(day) || [];
                if (!list.length) return null;
                return (
                  <div key={day} className="mb-2">
                    <div className="fw-semibold mb-1">
                      {t("cycleTemplatesPanel.day")} {day}
                    </div>
                    <div className="list-group">
                      {list.map((a) => (
                        <div key={a.id} className="list-group-item d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center gap-2">
                            <span className="badge text-bg-secondary">D{a.day}</span>
                            <img src={sportIcon(a.sport)} alt={a.sport} width={20} height={20} />
                            <div>
                              <div className="fw-semibold">{a.title}</div>
                              <small className="text-muted">
                                {METRICS.find((m) => m.value === a.metric)?.label}: {a.value} · {a.description}
                              </small>
                            </div>
                          </div>
                          <div className="d-flex gap-2">
                            <button type="button" className="btn btn-sm btn-success" onClick={() => startEditActivity(a.id)}>
                              {t("trainerLibraryManager.edit")}
                            </button>
                            <button type="button" className="btn btn-sm btn-danger" onClick={() => removeActivity(a.id)}>
                              {t("common.delete")}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        <div className="mt-3 d-flex gap-2">
          {!isEditing ? (
            <button type="button" className="btn btn-success" onClick={handleCreate}>
              {t("cycleTemplatesPanel.createButton")}
            </button>
          ) : (
            <>
              <button type="button" className="btn btn-primary" onClick={handleUpdate}>
                {t("cycleTemplatesPanel.saveChanges")}
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={handleCancelEdit}>
                {t("cycleTemplatesPanel.cancelEdit")}
              </button>
            </>
          )}
        </div>
      </form>

      {loading && <p>{t("cycleTemplatesPanel.loading")}</p>}
      {error && <p className="text-danger">{error}</p>}

      <div className="list-group">
        {templates.length === 0 && !loading && (
          <p className="text-muted">{t("cycleTemplatesPanel.noTemplatesYet")}</p>
        )}

        {templates.map((tpl) => {
          const daysArr = Array.isArray(tpl.days) ? tpl.days : [];
          const maxDayFromPlan = daysArr.reduce((m, d) => Math.max(m, Math.max(1, Number(d?.day ?? 0))), 0);
          const shadow = loadShadowForTemplate(tpl);
          const shadowDays = shadow?.defaultDays || (Array.isArray(shadow?.days) ? shadow.days.length : 0);
          const totalDays = Math.max(Number(tpl.defaultDays || 0), maxDayFromPlan, shadowDays);
          const hasPlan = totalDays > 0;

          return (
            <div key={tpl._id} className="list-group-item d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <img src={sportIcon(tpl.sport)} alt={tpl.sport} width={24} height={24} />
                <div>
                  <div className="fw-semibold">{tpl.name}</div>
                  <small className="text-muted d-block">
                    {tpl.distanceCategory || t("trainerTemplates.noCategory")} | {levelLabel(tpl.level)} |{" "}
                    {hasPlan
                      ? totalDays === 1
                        ? t("trainerTemplates.oneDay")
                        : t("trainerTemplates.multipleDays", { count: totalDays })
                      : t("trainerTemplates.noPlanning")}
                  </small>
                </div>
              </div>

              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-success"
                  onClick={() => handleEdit(tpl._id)}
                  disabled={loadingEditId === tpl._id}
                >
                  {loadingEditId === tpl._id
                    ? t("cycleTemplatesPanel.loadingShort")
                    : hasPlan
                    ? t("trainerLibraryManager.edit")
                    : t("trainerTemplates.configure")}
                </button>

                <button type="button" className="btn btn-sm btn-secondary" onClick={() => alert(t("cycleTemplatesPanel.alerts.copyPending"))}>
                  {t("cycleTemplatesPanel.copy")}
                </button>
                <button type="button" className="btn btn-sm btn-warning" onClick={() => alert(t("cycleTemplatesPanel.alerts.movePending"))}>
                  {t("cycleTemplatesPanel.move")}
                </button>

                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={async () => {
                    if (!window.confirm(t("cycleTemplatesPanel.confirmDeleteTemplate"))) return;
                    try {
                      setError("");
                      await api.delete(`/cycles/templates/${tpl._id}`, authConfig());
                      removeShadowForId(tpl._id);
                      fetchTemplates();
                    } catch (err) {
                      console.error(err);
                      const status = err?.response?.status;
                      if (status === 401) {
                        setError(t("cycleTemplatesPanel.errors.unauthenticatedDelete"));
                      } else {
                        alert(t("cycleTemplatesPanel.alerts.deleteError"));
                      }
                    }
                  }}
                >
                  {t("common.delete")}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <SportPickerModal
        open={sportModalOpen}
        value={newTemplate.sport}
        onPick={(sport) => setNewTemplate({ ...newTemplate, sport })}
        onClose={() => setSportModalOpen(false)}
        sports={SPORTS}
        title={t("activitySelector.selectActivityType")}
      />
      <SportPickerModal
        open={actSportModalOpen}
        value={formAct.sport}
        onPick={(sport) => setFormAct({ ...formAct, sport })}
        onClose={() => setActSportModalOpen(false)}
        sports={SPORTS}
        title={t("activitySelector.selectActivityType")}
      />
    </div>
  );
}

/* util id */
function randomId() {
  try {
    const b = new Uint32Array(1);
    crypto.getRandomValues(b);
    return b[0].toString(36);
  } catch {
    return Math.random().toString(36).slice(2);
  }
}